import type {
  MediaAccessStatusReturn,
  MediaType,
} from '@carboncopyco/types/desktop';
import { useCallback, useEffect, useState } from 'react';

interface UseSystemPermissionsReturnType {
  hasMicrophonePermission: boolean;
  hasCameraPermission: boolean;
  hasScreenRecordingPermission: boolean;
  alreadyAskedScreenRecording: () => Promise<boolean>;
  resetScreenRecordingPermission: () => Promise<void>;
  storePermissionRequest: () => Promise<void>;
}

const INITIAL_MEDIA_ACCESS_STATUS: MediaAccessStatusReturn = {
  microphone: 'unknown',
  camera: 'unknown',
  screen: 'unknown',
};

export const useSystemPermissions = (): UseSystemPermissionsReturnType => {
  const [mediaAccessStatus, setMediaAccessStatus] =
    useState<MediaAccessStatusReturn>(INITIAL_MEDIA_ACCESS_STATUS);

  useEffect(() => {
    (async () => {
      try {
        setMediaAccessStatus(await window.dsDesktop.app.getMediaAccessStatus());
      } catch (error) {
        setMediaAccessStatus(INITIAL_MEDIA_ACCESS_STATUS);
        console.error('Error in getting media access status', error);
      }
    })();
  }, []);

  const hasPermission = useCallback(
    (mediaType: MediaType) => mediaAccessStatus[mediaType] === 'granted',
    [mediaAccessStatus]
  );

  const alreadyAskedScreenRecording = () => {
    return window.dsDesktop.app.checkIfAlreadyAskedForScreenRecordingPermission();
  };

  const resetScreenRecordingPermission = () => {
    return window.dsDesktop.app.resetScreenRecordingPermission();
  };

  const storePermissionRequest = async () => {
    return window.dsDesktop.app.storePermissionRequest();
  };

  return {
    hasMicrophonePermission: hasPermission('microphone'),
    hasCameraPermission: hasPermission('camera'),
    hasScreenRecordingPermission: hasPermission('screen'),
    alreadyAskedScreenRecording,
    resetScreenRecordingPermission,
    storePermissionRequest,
  };
};
